<svelte:options runes={false} />

<script lang="ts">
	// Formatting
	import { format } from 'svelte-i18n';
	// Svelte
	import { createEventDispatcher, onMount } from 'svelte';
	// Types
	import type { SelectraQuestion } from 'src/types/Selectra';
	// Stores / actions
	import {
		startSelectraQualification,
		submitSelectraQualificationStep,
		finishSelectraQualification,
	} from 'src/stores/selectra';
	// Design system components
	import {
		Modal,
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
		Select,
		Input,
		Button,
		Alert,
	} from '@Shelly/shelly-design-system';
	// Helpers
	import {
		parseValidationRegex,
		shouldAdvanceQualificationStep,
		toSelectOptions,
		upsertQualificationStep,
	} from 'src/helpers/selectra_qualification_wizard_helpers';

	const dispatch = createEventDispatcher();

	export let showModal: boolean;

	let stepHistory: SelectraQuestion[] = [];
	let answeredFields: string[] = [];
	let inputs: Record<string, unknown> = {};
	let currentQuestion: SelectraQuestion = null;
	let selectValue: { title: string; value: string } = null;
	let textValue = '';
	let loading = true;
	let submitting = false;
	let error = false;

	$: activeStepIndex = answeredFields.length;
	$: isInputQuestion = currentQuestion?.type === 'input';
	$: validationRegex = parseValidationRegex(currentQuestion?.validation);
	$: isValidInput = !isInputQuestion || !validationRegex || validationRegex.test(textValue.trim());
	$: isFilled = isInputQuestion
		? textValue.trim().length > 0 && isValidInput
		: Boolean(selectValue?.value);

	const applyResponse = async (response) => {
		if (response?.done) {
			await finishSelectraQualification(response.inputs);
			dispatch('done');
			closeModal();
			return;
		}

		inputs = response?.inputs ?? {};
		currentQuestion = response?.questions?.[0] ?? null;
		selectValue = null;
		textValue = '';

		if (currentQuestion) {
			stepHistory = upsertQualificationStep(stepHistory, currentQuestion, answeredFields.length);
		}
	};

	const loadFirstStep = async () => {
		loading = true;
		error = false;
		stepHistory = [];
		answeredFields = [];
		inputs = {};

		try {
			const response = await startSelectraQualification();
			await applyResponse(response);
		} catch (e) {
			console.error('Failed to start Selectra qualification', e);
			error = true;
		} finally {
			loading = false;
		}
	};

	const goNext = async () => {
		if (!currentQuestion || !isFilled) return;

		submitting = true;
		error = false;
		const value = isInputQuestion ? textValue.trim() : selectValue?.value;
		const nextInputs = { ...inputs, [currentQuestion.field]: value };

		try {
			const response = await submitSelectraQualificationStep(nextInputs);
			const nextQuestion = response?.questions?.[0] ?? null;
			if (shouldAdvanceQualificationStep(currentQuestion, nextQuestion, Boolean(response?.done))) {
				answeredFields = [...answeredFields, currentQuestion.field];
			}
			await applyResponse(response);
		} catch (e) {
			console.error('Failed to submit Selectra qualification step', e);
			error = true;
		} finally {
			submitting = false;
		}
	};

	const goBack = async () => {
		if (answeredFields.length === 0) return;

		submitting = true;
		error = false;
		const lastField = answeredFields[answeredFields.length - 1];
		const previousFields = answeredFields.slice(0, -1);
		const previousValue = inputs[lastField];
		const isReturningToFirstStep = previousFields.length === 0;

		try {
			const response = isReturningToFirstStep
				? await startSelectraQualification()
				: await submitSelectraQualificationStep({ ...inputs, [lastField]: null });
			answeredFields = previousFields;
			await applyResponse(response);

			if (currentQuestion?.field === lastField) {
				if (currentQuestion.type === 'input') {
					textValue = String(previousValue ?? '');
				} else {
					const matchedOption = currentQuestion.options?.[String(previousValue)];
					selectValue = matchedOption
						? { title: matchedOption, value: String(previousValue) }
						: null;
				}
			}
		} catch (e) {
			console.error('Failed to go back in Selectra qualification', e);
			error = true;
		} finally {
			submitting = false;
		}
	};

	const closeModal = () => {
		showModal = false;
		dispatch('close');
	};

	const onClickWizardItem = async (index: number) => {
		if (index >= activeStepIndex) return;

		const stepsToRewind = activeStepIndex - index;
		for (let i = 0; i < stepsToRewind; i++) {
			await goBack();
		}
	};

	onMount(() => {
		loadFirstStep();
	});
</script>

<Modal
	showModal={Boolean(showModal)}
	title={$format('title_selectra_qualification', { default: 'Set up dynamic energy prices' })}
	backdropClose={false}
	on:close={closeModal}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each stepHistory as step, i (step.field)}
			<WizardItem
				title={step.label ?? step.field}
				active={i === activeStepIndex}
				disabled={i > activeStepIndex}
				indicatorColor="color-2"
				on:click={() => onClickWizardItem(i)}
			/>
		{/each}
	</Wizard>

	<svelte:fragment slot="content">
		<WizardContainer activeStepIndex={0} adaptiveHeight>
			<WizardContent active>
				{#if loading}
					<Alert type="info" heading={$format('label_loading', { default: 'Loading...' })} />
				{:else if error}
					<Alert
						type="danger"
						heading={$format('error_selectra_qualification_step', {
							default: 'Something went wrong. Please try again.',
						})}
					/>
				{:else if currentQuestion}
					{#key `${currentQuestion.field}:${currentQuestion.label ?? ''}`}
						<div class="mb-s">
							{#if isInputQuestion}
								<Input
									id={`selectra-question-${currentQuestion.field}`}
									aria-labelledby={`selectra-question-${currentQuestion.field}`}
									label={currentQuestion.label ?? currentQuestion.field}
									placeholder={currentQuestion.placeholder}
									type="text"
									bind:value={textValue}
								/>
							{:else}
								<Select
									id={`selectra-question-${currentQuestion.field}`}
									aria-labelledby={`selectra-question-${currentQuestion.field}`}
									label={currentQuestion.label ?? currentQuestion.field}
									width="full"
									icon={null}
									options={toSelectOptions(currentQuestion.options)}
									bind:value={selectValue}
								/>
							{/if}
						</div>
					{/key}
				{/if}
			</WizardContent>
		</WizardContainer>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStepIndex > 0}
			<Button
				class="mr-s"
				variant="secondary"
				disabled={submitting}
				on:click={goBack}
				aria-label={$format('button_back', { default: 'Back' })}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			disabled={!isFilled || submitting || loading}
			loading={submitting}
			on:click={goNext}
			aria-label={$format('button_next', { default: 'Next' })}
		>
			{$format('button_next', { default: 'Next' })}
		</Button>
	</div>
</Modal>
